/**
 * usePermissions Hook
 * ===================
 * Hook for permission-based access control
 */

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useOrg } from './useOrg';

/**
 * Hook for permission-based access control
 * @returns {Object} Permission checking functions
 */
export const usePermissions = () => {
  const { user, hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole } = useAuth();
  const { activeOrg } = useOrg();

  // Memoize permission lists
  const permissions = useMemo(() => user?.permissions || [], [user]);
  const roles = useMemo(() => user?.roles || [], [user]);
  const primaryRole = useMemo(() => user?.primary_role || null, [user]);

  const isSuperAdmin = useMemo(() => {
    return user?.primary_role === 'super_admin' || roles.includes('super_admin');
  }, [user, roles]);

  const isOrgAdmin = useMemo(() => {
    return activeOrg?.role === 'admin' || isSuperAdmin;
  }, [activeOrg, isSuperAdmin]);

  const isAdmin = useMemo(() => {
    return roles.includes('admin') || roles.includes('super_admin') || permissions.includes('*');
  }, [roles, permissions]);

  // Permission checking functions
  const can = useMemo(
    () => ({
      // Lead permissions
      createLead: () => hasPermission('leads:create'),
      readLead: () => hasPermission('leads:read'),
      updateLead: () => hasPermission('leads:update'),
      deleteLead: () => hasPermission('leads:delete'),

      // Discovery permissions
      submitDiscovery: () => hasPermission('discovery:submit'),
      readDiscovery: () => hasPermission('discovery:read'),

      // Proposal permissions
      generateProposal: () => hasPermission('proposals:generate'),
      readProposal: () => hasPermission('proposals:read'),
      sendProposal: () => hasPermission('proposals:send'),
      reviewProposal: () => hasPermission('proposals:review'),
      approveProposal: () => hasPermission('proposals:approve'),
      submitForReview: () => hasPermission('proposals:submit_for_review'),
      sendToClient: () => hasPermission('proposals:send_to_client'),

      // Analysis permissions
      readAnalysis: () => hasPermission('analysis:read'),
      updateAnalysis: () => hasPermission('analysis:update'),

      // Compliance permissions
      createCompliance: () => hasPermission('compliance:create'),
      readCompliance: () => hasPermission('compliance:read'),
      updateCompliance: () => hasPermission('compliance:update'),

      // QA permissions
      createQA: () => hasPermission('qa:create'),
      readQA: () => hasPermission('qa:read'),
      updateQA: () => hasPermission('qa:update'),

      // Meeting permissions
      createMeeting: () => hasPermission('meetings:create'),
      readMeeting: () => hasPermission('meetings:read'),
      updateMeeting: () => hasPermission('meetings:update'),

      // Questionnaire permissions
      readQuestionnaire: () => hasPermission('questionnaires:read'),
      editQuestionnaire: () => hasPermission('questionnaires:edit'),
      completeQuestionnaire: () => hasPermission('questionnaires:complete'),
      regenerateQuestionnaire: () => hasPermission('questionnaires:regenerate'),

      // Audit log permissions
      readAuditLogs: () => hasPermission('audit_logs:read'),

      // Dashboard permissions
      readDashboard: () => hasPermission('dashboard:read'),

      // User management permissions (admin only)
      createUser: () => hasPermission('users:create'),
      readUser: () => hasPermission('users:read'),
      updateUser: () => hasPermission('users:update'),
      deleteUser: () => hasPermission('users:delete'),
      manageRoles: () => hasPermission('roles:manage'),

      // System prompt permissions
      manageSystemPrompts: () => hasPermission('system:admin'),
      readSystemPrompts: () => hasPermission('system:read'),
    }),
    [hasPermission]
  );

  const is = useMemo(
    () => ({
      superAdmin: () => isSuperAdmin,
      admin: () => hasRole('admin'),
      bdTeam: () => hasRole('bd_team'),
      complianceOfficer: () => hasRole('compliance_officer'),
      viewer: () => hasRole('viewer'),
    }),
    [hasRole, isSuperAdmin]
  );

  return {
    permissions,
    roles,
    primaryRole,
    isAdmin,
    isSuperAdmin,
    isOrgAdmin,
    can,
    is,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  };
};

export default usePermissions;

